/* ──────────────────────────────────────────────────────────────
 *  SampleTable — 5-column comparison table for selected sample
 *
 *  Columns: Row Label | Target | Design | vs Target (%) | Expected Test Values
 *  Adapts row structure per bushing type (identical to Python logic).
 *
 *  Also persists the computed "Expected Test Values" column into
 *  useSampleStore, keyed by sample ID, so the export flow
 *  (useExportJobs → /jobs/export → PDF/PPT generators) can reuse
 *  the exact same values without recomputing the FEA→test inverse
 *  solver call or duplicating the D1/BYD passthrough logic.
 * ────────────────────────────────────────────────────────────── */

import React, { useMemo, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { useBushingStore } from '@/stores/useBushingStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { useExpectedTestValues } from '@/hooks/useExpectedTestValues';
import type { BushingType, ParamName } from '@/types/bushing';
import type { SampleRow } from '@/types/sample';

/* ── Types ────────────────────────────────────────────────── */

interface TableItem {
  metric: string;
  target: string;
  design: string;
  vsTarget: string;
  expected: string;
  /** Color for vsTarget cell (hex or null). */
  vsTargetColor: string | null;
}

/* ── Helpers ──────────────────────────────────────────────── */

function getMetrics(bushingType: BushingType): ParamName[] {
  switch (bushingType) {
    case 'BYD EV Voided':
      return ['Cy', 'Cz', 'Cx'];
    case 'BYD EV Solid':
      return ['Cz', 'Cx'];
    case 'D1':
      return ['Cy', 'Cz', 'Cx', 'Torsion', 'Conical Void', 'Conical Solid'];
    default:
      // A1, B1, C1
      return ['Cz', 'Cx', 'Torsion', 'Conical'];
  }
}

function getIntegerParams(bushingType: BushingType): Set<string> {
  switch (bushingType) {
    case 'BYD EV Voided':
    case 'D1':
      return new Set(['Cx', 'Cy', 'Cz']);
    case 'BYD EV Solid':
      return new Set(['Cx', 'Cz']);
    default:
      return new Set(['Cx', 'Cz']);
  }
}

function shouldHideExpected(bushingType: BushingType): boolean {
  return bushingType === 'D1' || bushingType === 'BYD EV Voided';
}

function fmtNum(val: number | null | undefined, integer: boolean): string {
  if (val == null || !isFinite(val)) return '';
  return integer ? Math.round(val).toString() : val.toFixed(1);
}

function fmtPct(val: number | null): string {
  if (val == null || !isFinite(val)) return '';
  return `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`;
}

function computePercentError(
  design: number | null,
  target: number | null,
): number | null {
  if (design == null || target == null || target === 0) return null;
  return ((design - target) / target) * 100;
}

function getVsTargetColor(metric: string, pct: number | null): string | null {
  if (pct == null) return null;
  if (metric.toLowerCase().includes('torsion') && pct < 0) return '#b7ebc5';
  if (Math.abs(pct) > 11) return '#f9fabe';
  return '#b7ebc5';
}

/** Map param name → target key in TargetInputs.targets */
function getTargetKey(param: ParamName): string {
  // All bushing types store targets keyed by param name directly
  return param;
}

/**
 * Compute the "expected test value" for a single metric, using the
 * same rule as the table cell: D1/BYD types pass the design value
 * straight through (no FEA→test conversion); A1/B1/C1 pull from the
 * backend inverse-solver result; Conical is never provided by the
 * solver and stays null; Conical Void has no data at all.
 */
function computeExpectedValue(
  metric: ParamName,
  bushingType: BushingType,
  designVal: number | null,
  expectedTestValues: { Cx: number | null; Cz: number | null; Torsion: number | null; Conical: number | null },
): number | null {
  if (metric === 'Conical Void') return null;

  if (
    bushingType === 'D1' ||
    bushingType === 'BYD EV Voided' ||
    bushingType === 'BYD EV Solid'
  ) {
    return designVal;
  }

  if (metric === 'Conical') {
    // Conical is not provided by get_test_vals — stays null
    return null;
  }

  // Cx, Cz, Torsion from the inverse solver
  return expectedTestValues[metric as 'Cx' | 'Cz' | 'Torsion'] ?? null;
}

/* ── Component ────────────────────────────────────────────── */

const SampleTable: React.FC = () => {
  const bushingType = useBushingStore((s) => s.bushingType);
  const displayLabels = useBushingStore((s) => s.displayLabels);
  const snapshotTargets = useSampleStore((s) => s.snapshotTargets);
  const currentSelectedId = useSampleStore((s) => s.currentSelectedId);
  const genRowsById = useSampleStore((s) => s.genRowsById);
  const allSamples = useSampleStore((s) => s.allSamples);

  const hideExpected = shouldHideExpected(bushingType);

  const selectedRow: SampleRow | null = useMemo(() => {
    if (!currentSelectedId) return null;
    if (currentSelectedId.startsWith('N')) {
      return genRowsById[currentSelectedId] ?? null;
    }
    return allSamples.find((r) => r.id === currentSelectedId) ?? null;
  }, [currentSelectedId, genRowsById, allSamples]);

  const { expected: expectedTestValues, loading: expectedLoading } = useExpectedTestValues(selectedRow);

  const items: TableItem[] = useMemo(() => {
    const metrics = getMetrics(bushingType);
    const intParams = getIntegerParams(bushingType);

    return metrics.map((m) => {
      const isInt = intParams.has(m);
      const label = displayLabels[m] ?? m;
      const targetKey = getTargetKey(m);
      const targetVal = snapshotTargets[targetKey] ?? null;

      let designVal: number | null = null;
      if (selectedRow) {
        if (m === 'Conical Solid') {
          const raw = selectedRow['Conical'] ?? selectedRow[m];
          designVal = typeof raw === 'number' ? raw : null;
        } else if (m === 'Conical Void') {
          designVal = null; // explicitly no data
        } else {
          const raw = selectedRow[m];
          designVal = typeof raw === 'number' ? raw : null;
        }
      }

      const pct = computePercentError(designVal, targetVal);
      const color = m === 'Conical Void' ? null : getVsTargetColor(label, pct);

      const expectedVal = computeExpectedValue(m, bushingType, designVal, expectedTestValues);

      return {
        metric: label,
        target: m === 'Conical Void' ? '' : fmtNum(targetVal, isInt),
        design: m === 'Conical Void' ? 'no data' : fmtNum(designVal, isInt),
        vsTarget: m === 'Conical Void' ? '' : fmtPct(pct),
        expected: hideExpected ? '' : (expectedLoading ? '…' : fmtNum(expectedVal, isInt)),
        vsTargetColor: color,
      };
    });
  }, [bushingType, displayLabels, snapshotTargets, selectedRow, hideExpected, expectedTestValues, expectedLoading]);

  /**
   * Metric → expected-value map for persistence into useSampleStore.
   * Built with the same computeExpectedValue rule as `items` above so
   * the stored values are always identical to what's on screen — kept
   * as a separate memo (rather than derived from `items`, which holds
   * pre-formatted display strings) so the store gets raw numbers the
   * PDF/PPT generators can format however they need.
   */
  const expectedMap: Record<string, number | null> = useMemo(() => {
    const metrics = getMetrics(bushingType);
    const map: Record<string, number | null> = {};

    metrics.forEach((m) => {
      if (m === 'Conical Void') return; // no data for this row

      let designVal: number | null = null;
      if (selectedRow) {
        const raw = m === 'Conical Solid'
          ? (selectedRow['Conical'] ?? selectedRow[m])
          : selectedRow[m];
        designVal = typeof raw === 'number' ? raw : null;
      }

      map[m] = computeExpectedValue(m, bushingType, designVal, expectedTestValues);
    });

    return map;
  }, [bushingType, selectedRow, expectedTestValues]);

  // Persist to the sample store so the export flow (useExportJobs) can
  // read it later, independent of whether SampleTable is still mounted.
  // Skipped while expectedLoading is true so we never cache a stale
  // in-flight state (e.g. mid-debounce for the A1/B1/C1 inverse solver
  // call) under the sample's key.
  useEffect(() => {
    if (!currentSelectedId || expectedLoading) return;
    useSampleStore.getState().setExpectedTestValuesForSample(currentSelectedId, expectedMap);
  }, [currentSelectedId, expectedMap, expectedLoading]);

  /* ── Placeholder when no sample selected ─────────────── */
  if (!currentSelectedId) {
    return (
      <TableContainer sx={tableContainerSx}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx} />
              <TableCell sx={thSx}>Target</TableCell>
              <TableCell sx={thSx}>Design</TableCell>
              <TableCell sx={thSx}>vs Target</TableCell>
              {!hideExpected && (
                <TableCell sx={thSx}>Expected Test Values</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Hint</TableCell>
              <TableCell colSpan={hideExpected ? 3 : 4}>
                <Typography variant="body2" color="text.secondary">
                  Select a New (orange) sample in the plot.
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer sx={tableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx} />
            <TableCell sx={thSx}>Target</TableCell>
            <TableCell sx={thSx}>Design</TableCell>
            <TableCell sx={thSx}>vs Target</TableCell>
            {!hideExpected && (
              <TableCell sx={thSx}>Expected Test Values</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.metric}>
              <TableCell sx={{ ...tdSx, fontWeight: 600 }}>
                {item.metric}
              </TableCell>
              <TableCell sx={tdSx}>{item.target}</TableCell>
              <TableCell sx={tdSx}>{item.design}</TableCell>
              <TableCell
                sx={{
                  ...tdSx,
                  backgroundColor: item.vsTargetColor ?? 'transparent',
                }}
              >
                {item.vsTarget}
              </TableCell>
              {!hideExpected && (
                <TableCell sx={tdSx}>{item.expected}</TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/* ── Shared styles ────────────────────────────────────────── */

const tableContainerSx = {   
  width: '98%',           
  flex : 1,
  alignSelf: 'center',
  mx: 'auto',
  overflow: 'auto',
  border: '1px solid #ffffff', 
  borderRadius: 1,           
  backgroundColor: '#ffffff',
  boxShadow: 'none',
} as const;

const thSx = {
  fontWeight: 600,
  fontSize: '0.9rem',
  color: 'text.secondary',
  backgroundColor: '#f5f5f7',
  borderBottom: '1px solid #dcdcdc',
  py: 0.7,
  px: 1,
} as const;

const tdSx = {
  fontSize: '0.9rem',
  backgroundColor: '#ffff',
  color: 'text.secondary',
  borderBottom: '1px solid #dcdcdc',
  py: 1,
  px: 1,
} as const;

export default React.memo(SampleTable);
